import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { AppInitService } from './services/app/app-init.service';
import AuthInterceptor from './services/auth/auth-interceptor';
import { RolePreloadingStrategy } from './services/preloading/role-preloading.strategy';
import { ReadOnlyInterceptor } from './services/read-only/read-only.interceptor';
import { TranslationService } from './services/translation/translation.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(RolePreloadingStrategy)),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(MatDialogModule),

    // ngx-translate configuration
    importProvidersFrom(TranslateModule.forRoot()),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),

    // Initialize translations before app starts.
    // On the login page, load translations in the background so the login form
    // renders immediately without waiting for the HTTP request to complete.
    provideAppInitializer(() => {
      const translationService = inject(TranslationService);
      if (window.location.pathname === '/login') {
        translationService.initializeLanguage();
        return;
      }
      return translationService.initializeLanguage();
    }),

    // HTTP Interceptors
    // ReadOnlyInterceptor runs first so blocked writes never reach the auth
    // interceptor (no token refresh, no 401 retry for a request we reject).
    { provide: HTTP_INTERCEPTORS, useClass: ReadOnlyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    // App initialization - equivalent to your old AppModule setup
    provideAppInitializer(() => {
      const initializerFn = ((init: AppInitService) => init.initializeApp())(
        inject(AppInitService)
      );
      return initializerFn();
    }),
  ],
};
